import { useState } from 'react';
import {
  MessagingProvisioningStatus,
  ProvisioningAction,
  OPERATOR_REGISTRATION_LABELS,
  type AdvanceProvisioningRequest,
  type MessagingProfileDto,
} from '@signalvote/shared';
import {
  useMessagingProfile,
  useCreateMessagingProfile,
  useUpdateMessagingProfile,
  useAdvanceProvisioning,
} from '@/hooks/queries';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { ReadNotice } from '@/components/ui';
import { PendingButton } from '@/components/ui/pending-button';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CommitteeFactsCard } from './CommitteeFactsCard';

/**
 * Platform-admin surface for the Campaign Messaging Profile. It mirrors the entity's
 * structural split: the top card records the portable Campaign Verify pass, the
 * bottom card captures the use-case fields and drives the disposable 10DLC/TCR
 * provisioning spine by hand. There is no vendor/TCR API — every id is operator-typed.
 */
export function ProfilePageContent() {
  const read = useMessagingProfile();
  const { mutateAsync: createProfile, isPending: creating } = useCreateMessagingProfile();

  if (read.status === 'failed' || read.status === 'offline') {
    return (
      <div className="p-6">
        <ReadNotice read={read} subject="the messaging profile" />
      </div>
    );
  }
  if (read.status !== 'ready') {
    return <div className="p-6 text-muted-foreground">Loading messaging profile…</div>;
  }
  const profile = read.data;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Messaging profile</h1>
        <p className="text-sm text-muted-foreground">
          Record the campaign's Campaign Verify pass and drive its 10DLC/TCR
          provisioning through the vendor by hand. The vendor's own forms remain the
          system of record — only the essentials are stored here.
        </p>
      </div>

      {profile == null ? (
        <Card>
          <CardHeader>
            <CardTitle>No messaging profile yet</CardTitle>
            <CardDescription>
              Create the profile to start recording the pass and provisioning.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <PendingButton pending={creating} onClick={() => createProfile()}>
              Create messaging profile
            </PendingButton>
          </CardFooter>
        </Card>
      ) : (
        <>
          <CommitteeFactsCard />
          <CampaignVerifyCard profile={profile} />
          <ProvisioningCard profile={profile} />
        </>
      )}
    </div>
  );
}

// ============================================
// Portable Campaign Verify pass
// ============================================

function CampaignVerifyCard({ profile }: { profile: MessagingProfileDto }) {
  const { mutateAsync: update, isPending } = useUpdateMessagingProfile();
  const [passId, setPassId] = useState(profile.campaignVerify.passId ?? '');
  const [expiryDate, setExpiryDate] = useState(profile.campaignVerify.expiryDate ?? '');
  const [needsRenewal, setNeedsRenewal] = useState(profile.campaignVerify.needsRenewal);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      await update({
        campaignVerifyPassId: passId || null,
        campaignVerifyExpiryDate: expiryDate || null,
        campaignVerifyNeedsRenewal: needsRenewal,
        useCaseCategory: null,
        sampleMessage: null,
        optInDescription: null,
        estimatedMonthlyVolume: null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save pass');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Campaign Verify pass</CardTitle>
        <CardDescription>
          The portable pass — a reference and expiry that travels with the campaign
          across vendors. This is a fact plus an expiry, not a workflow.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="cv-pass-id">Pass reference</Label>
          <Input
            id="cv-pass-id"
            value={passId}
            onChange={(e) => setPassId(e.target.value)}
            placeholder="e.g. CV-PASS-0001"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cv-expiry">Expiry date</Label>
          <Input
            id="cv-expiry"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="cv-needs-renewal">Needs renewal</Label>
          <Switch id="cv-needs-renewal" checked={needsRenewal} onCheckedChange={setNeedsRenewal} />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
      <CardFooter>
        <PendingButton pending={isPending} onClick={save}>
          Save pass
        </PendingButton>
      </CardFooter>
    </Card>
  );
}

// ============================================
// Disposable Messaging Provisioning spine
// ============================================

function ProvisioningCard({ profile }: { profile: MessagingProfileDto }) {
  const { mutateAsync: update, isPending: saving } = useUpdateMessagingProfile();
  const { mutateAsync: advance, isPending: advancing } = useAdvanceProvisioning();
  const { provisioning, concurrencyVersion } = profile;

  const [useCaseCategory, setUseCaseCategory] = useState(provisioning.useCaseCategory ?? '');
  const [sampleMessage, setSampleMessage] = useState(provisioning.sampleMessage ?? '');
  const [optInDescription, setOptInDescription] = useState(provisioning.optInDescription ?? '');
  const [estimatedMonthlyVolume, setEstimatedMonthlyVolume] = useState(
    provisioning.estimatedMonthlyVolume?.toString() ?? '',
  );
  const [vendor, setVendor] = useState(provisioning.vendor ?? '');
  const [tcrBrandId, setTcrBrandId] = useState(provisioning.tcrBrandId ?? '');
  const [tcrCampaignId, setTcrCampaignId] = useState(provisioning.tcrCampaignId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const status = provisioning.status;
  const isTerminal =
    status === MessagingProvisioningStatus.Provisioned ||
    status === MessagingProvisioningStatus.Rejected ||
    status === MessagingProvisioningStatus.Failed;
  // The use-case fields stay correctable until the package is handed to the vendor;
  // after SubmitToVendor the info is out the door, so it locks.
  const useCaseEditable =
    status === MessagingProvisioningStatus.NotStarted ||
    status === MessagingProvisioningStatus.InfoCollected;

  const saveInfo = async () => {
    setError(null);
    try {
      const volume = estimatedMonthlyVolume.trim();
      await update({
        campaignVerifyPassId: null,
        campaignVerifyExpiryDate: null,
        campaignVerifyNeedsRenewal: null,
        useCaseCategory: useCaseCategory || null,
        sampleMessage: sampleMessage || null,
        optInDescription: optInDescription || null,
        estimatedMonthlyVolume: volume ? Number(volume) : null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save use-case fields');
    }
  };

  const runAdvance = async (
    action: ProvisioningAction,
    extra: Partial<Pick<AdvanceProvisioningRequest, 'vendor' | 'tcrBrandId' | 'tcrCampaignId' | 'rejectionReason'>> = {},
  ) => {
    setError(null);
    try {
      await advance({
        action,
        vendor: null,
        tcrBrandId: null,
        tcrCampaignId: null,
        rejectionReason: null,
        expectedConcurrencyVersion: concurrencyVersion,
        ...extra,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to advance provisioning');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Messaging provisioning</CardTitle>
          <Badge variant="secondary">{OPERATOR_REGISTRATION_LABELS[status]}</Badge>
        </div>
        <CardDescription>
          The disposable, vendor-scoped record. Capture the use-case fields, then
          advance the spine one deliberate step at a time as you drive the vendor's
          forms by hand.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Use-case fields — editable until submitted to the vendor. */}
        <fieldset disabled={!useCaseEditable} className="space-y-4 disabled:opacity-60">
          <div className="space-y-2">
            <Label htmlFor="use-case-category">Use-case category</Label>
            <Input
              id="use-case-category"
              value={useCaseCategory}
              onChange={(e) => setUseCaseCategory(e.target.value)}
              placeholder="e.g. Political"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sample-message">Sample message</Label>
            <textarea
              id="sample-message"
              value={sampleMessage}
              onChange={(e) => setSampleMessage(e.target.value)}
              rows={2}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="opt-in-description">Opt-in description</Label>
            <textarea
              id="opt-in-description"
              value={optInDescription}
              onChange={(e) => setOptInDescription(e.target.value)}
              rows={2}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="monthly-volume">Estimated monthly volume</Label>
            <Input
              id="monthly-volume"
              type="number"
              min={0}
              value={estimatedMonthlyVolume}
              onChange={(e) => setEstimatedMonthlyVolume(e.target.value)}
              placeholder="e.g. 5000"
            />
          </div>
          {useCaseEditable && (
            <PendingButton pending={saving} onClick={saveInfo} variant="outline">
              Save use-case fields
            </PendingButton>
          )}
        </fieldset>

        {/* Captured provisioning facts — read-only, shown once stamped by a transition. */}
        {(provisioning.vendor || provisioning.tcrBrandId) && (
          <div className="rounded-md border border-border p-3 text-sm">
            {provisioning.vendor && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Vendor</span>
                <span>{provisioning.vendor}</span>
              </div>
            )}
            {provisioning.tcrBrandId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">TCR brand id</span>
                <span>{provisioning.tcrBrandId}</span>
              </div>
            )}
            {provisioning.tcrCampaignId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">TCR campaign id</span>
                <span>{provisioning.tcrCampaignId}</span>
              </div>
            )}
          </div>
        )}

        {/* Per-status advance control — the vendor-facing data is typed at the step that needs it. */}
        {status === MessagingProvisioningStatus.InfoCollected && (
          <div className="space-y-2">
            <Label htmlFor="vendor">Vendor</Label>
            <Input
              id="vendor"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. Twilio"
            />
          </div>
        )}
        {status === MessagingProvisioningStatus.SubmittedToVendor && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="tcr-brand-id">TCR brand id</Label>
              <Input
                id="tcr-brand-id"
                value={tcrBrandId}
                onChange={(e) => setTcrBrandId(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tcr-campaign-id">TCR campaign id</Label>
              <Input
                id="tcr-campaign-id"
                value={tcrCampaignId}
                onChange={(e) => setTcrCampaignId(e.target.value)}
              />
            </div>
          </div>
        )}

        {status === MessagingProvisioningStatus.Rejected && provisioning.rejectionReason && (
          <p className="rounded-md border border-border p-3 text-sm">
            <span className="text-muted-foreground">Rejected because: </span>
            {provisioning.rejectionReason}
          </p>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>

      {!isTerminal && (
        <CardFooter className="flex flex-wrap gap-2">
          {status === MessagingProvisioningStatus.NotStarted && (
            <PendingButton
              pending={advancing}
              onClick={() => runAdvance(ProvisioningAction.CollectInfo)}
            >
              Collect info
            </PendingButton>
          )}
          {status === MessagingProvisioningStatus.InfoCollected && (
            <PendingButton
              pending={advancing}
              onClick={() => runAdvance(ProvisioningAction.SubmitToVendor, { vendor })}
            >
              Submit to vendor
            </PendingButton>
          )}
          {status === MessagingProvisioningStatus.SubmittedToVendor && (
            <PendingButton
              pending={advancing}
              onClick={() =>
                runAdvance(ProvisioningAction.MarkProvisioned, { tcrBrandId, tcrCampaignId })
              }
            >
              Mark provisioned
            </PendingButton>
          )}
          <Button
            variant="outline"
            disabled={advancing}
            onClick={() => {
              setRejectReason('');
              setRejecting(true);
            }}
          >
            Reject
          </Button>
          <Button
            variant="outline"
            disabled={advancing}
            onClick={() => runAdvance(ProvisioningAction.Fail)}
          >
            Fail
          </Button>
        </CardFooter>
      )}
      <Dialog open={rejecting} onOpenChange={setRejecting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject registration</DialogTitle>
            <DialogDescription>The campaign sees this reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason</Label>
            <textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              disabled={advancing || !rejectReason.trim()}
              onClick={async () => {
                await runAdvance(ProvisioningAction.Reject, { rejectionReason: rejectReason.trim() });
                setRejecting(false);
              }}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
